import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Typography, Space, Button, Dropdown } from 'antd';
import { RollbackOutlined, DownOutlined, SettingOutlined, EyeOutlined } from '@ant-design/icons';
import leadReturnsService from '../../services/leadReturns';
import { useNavigate } from 'react-router-dom';

const UserReturnsPage = () => {
  const navigate = useNavigate();
  const { data: returns, isLoading } = useQuery({
    queryKey: ['returns'],
    queryFn: () => leadReturnsService.getReturns()
  });

  const [expandedRowKeys, setExpandedRowKeys] = React.useState([]);

  const handleViewLead = (record) => {
    navigate(`/purchases/${record.lead_purchase?.id}`);
  };

  const handleExpandClick = (record) => {
    const key = record.id;
    setExpandedRowKeys(expandedRowKeys.includes(key) 
      ? expandedRowKeys.filter(k => k !== key)
      : [...expandedRowKeys, key]
    );
  };

  const columns = [
    {
      title: 'Purchase Details',
      key: 'purchase',
      render: (_, record) => {
        const purchase = record.lead_purchase;
        if (!purchase) return 'N/A';
        return (
          <Space>
            <span>Transaction ID: {purchase.transaction_id}</span>
          </Space>
        );
      },
    },
    {
      title: 'Return Details',
      key: 'return',
      render: (_, record) => (
        <Button 
          type="link" 
          onClick={() => handleExpandClick(record)}
          icon={<DownOutlined rotate={expandedRowKeys.includes(record.id) ? 180 : 0} />}
        >
          <Tag color={
            record.status === 'pending' ? 'warning' :
            record.status === 'approved' ? 'success' : 'error'
          }>
            {record.status.toUpperCase()}
          </Tag>
        </Button>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'viewLead',
            icon: <EyeOutlined />,
            label: 'View Lead Details',
            onClick: () => handleViewLead(record)
          }
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
          >
            <Button 
              type="text" 
              icon={<SettingOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      }
    }
  ];

  const expandedRowRender = (record) => {
    return (
      <div style={{ padding: '16px' }}>
        <Table
          columns={[
            {
              title: 'Field',
              dataIndex: 'field',
              key: 'field',
              width: '30%',
            },
            {
              title: 'Value',
              dataIndex: 'value',
              key: 'value',
              width: '70%',
              render: (value, record) => {
                if (record.isHeader) {
                  return <Typography.Text strong>{value}</Typography.Text>;
                }
                if (record.field === 'Status') {
                  return (
                    <Tag color={
                      value === 'pending' ? 'warning' :
                      value === 'approved' ? 'success' : 'error'
                    }>
                      {value.toUpperCase()}
                    </Tag>
                  );
                }
                if (record.field === 'Requested At' || record.field === 'Processed At') {
                  return new Date(value).toLocaleString();
                }
                return value;
              }
            }
          ]}
          dataSource={[
            { field: 'Return Request Details', value: '', isHeader: true },
            { field: 'Return Reason', value: record.return_reason },
            { field: 'Status', value: record.status },
            { field: 'Requested At', value: record.created_at },
            { field: 'Processed At', value: record.processed_at || 'Not processed yet' },
            { field: 'Admin Response', value: record.admin_response || 'No response yet' },
            { field: 'Purchase Information', value: '', isHeader: true },
            { field: 'Transaction ID', value: record.lead_purchase?.transaction_id || 'N/A' },
            { field: 'Amount', value: record.lead_purchase?.amount ? `$${record.lead_purchase.amount}` : 'N/A' },
            { field: 'Payment Status', value: record.lead_purchase?.payment_status ? 'Paid' : 'Unpaid' },
            { field: 'Purchase Date', value: record.lead_purchase?.created_at ? new Date(record.lead_purchase.created_at).toLocaleString() : 'N/A' }
          ]}
          pagination={false}
          rowKey={(record) => record.field}
          onRow={(record) => ({
            style: record.isHeader ? { 
              background: '#f5f5f5',
              fontWeight: 'bold'
            } : {}
          })}
          showHeader={false}
          size="small"
          style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
        />
      </div>
    );
  };

  return (
    <div className="page-container" style={{
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      minHeight: '100vh',
      padding: '24px'
    }}>
      <div className="page-header" style={{
        marginBottom: '32px',
        padding: '24px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle at top right, rgba(24,144,255,0.1) 0%, rgba(24,144,255,0) 70%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
            transition: 'transform 0.3s ease'
          }}>
            <RollbackOutlined style={{ fontSize: '20px', color: '#fff' }} />
          </div>
          <div>
            <Typography.Title level={2} style={{
              margin: 0,
              color: '#1f1f1f',
              background: 'linear-gradient(135deg, #1f1f1f 0%, #434343 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '24px'
            }}>
              My Return Requests
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              View and track your return requests
            </Typography.Text>
          </div>
        </div>
      </div>

      <Card className="shadow-sm" style={{ 
        borderRadius: '12px', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
      }}>
        <Table
          columns={columns}
          dataSource={returns?.data}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1200 }}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            expandRowByClick: false,
            expandIcon: () => null
          }}
          pagination={{
            total: returns?.total,
            pageSize: returns?.per_page,
            current: returns?.current_page,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

    </div>
  );
};

export default UserReturnsPage; 